(() => {
  const DEFAULT_TOAST_OPTIONS = {
    type: "info",
    position: "top-right",
    fadeInMs: 160,
    visibleMs: 2200,
    fadeOutMs: 180,
    backgroundColor: "",
    textColor: "",
    icon: "",
    iconHtml: "",
    actionText: "",
    actionUrl: "",
    actionTarget: "_self",
    showClose: true,
  };

  let toastRoot = null;
  let activeToast = null;
  const VALID_TOAST_TYPES = ["success", "error", "warning", "info", "custom"];

  const ensureToastRoot = () => {
    if (toastRoot) return toastRoot;
    toastRoot = document.createElement("div");
    toastRoot.className = "toasty-toast-root";
    document.body.appendChild(toastRoot);
    return toastRoot;
  };

  const removeActiveToast = () => {
    if (!activeToast) return;
    if (activeToast.hideTimer) {
      clearTimeout(activeToast.hideTimer);
    }
    if (activeToast.cleanupTimer) {
      clearTimeout(activeToast.cleanupTimer);
    }
    activeToast.node.remove();
    activeToast = null;
  };

  const hideToast = (toast, options) => {
    if (!toast || toast.dataset.hiding === "1") return;
    toast.dataset.hiding = "1";
    toast.classList.remove("is-visible");
    toast.classList.add("is-hiding");

    const cleanupDelay = Math.max(0, options.fadeOutMs);
    const cleanupTimer = window.setTimeout(() => {
      toast.remove();
      if (activeToast && activeToast.node === toast) {
        activeToast = null;
      }
    }, cleanupDelay);

    if (activeToast && activeToast.node === toast) {
      activeToast.cleanupTimer = cleanupTimer;
    }
  };

  const getIconHtmlByName = (iconName) => {
    if (!iconName) return "";
    const iconNode = document.querySelector(
      `[data-toasty-icon-name="${iconName}"]`,
    );
    return iconNode ? iconNode.innerHTML : "";
  };

  const closeToastOnClick = (toast, options) => {
    if (activeToast && activeToast.node === toast && activeToast.hideTimer) {
      clearTimeout(activeToast.hideTimer);
      activeToast.hideTimer = null;
    }
    hideToast(toast, options);
  };

  const show = (opts = {}) => {
    const options = { ...DEFAULT_TOAST_OPTIONS, ...opts };
    if (!options.message) return;
    const normalizedType = VALID_TOAST_TYPES.includes(options.type)
      ? options.type
      : "info";
    options.type = normalizedType;

    const root = ensureToastRoot();
    removeActiveToast();

    const toast = document.createElement("div");
    toast.className = `toasty-toast toasty-toast--${options.position} toasty-toast--${options.type}`;
    toast.style.setProperty(
      "--toasty-toast-fade-in-ms",
      `${options.fadeInMs}ms`,
    );
    toast.style.setProperty(
      "--toasty-toast-fade-out-ms",
      `${options.fadeOutMs}ms`,
    );

    if (options.type === "custom") {
      if (options.backgroundColor) {
        toast.style.backgroundColor = options.backgroundColor;
      }
      if (options.textColor) {
        toast.style.color = options.textColor;
      }
    }

    if (options.iconHtml) {
      const icon = document.createElement("span");
      icon.className = "toasty-toast__icon";
      icon.innerHTML = options.iconHtml;
      toast.appendChild(icon);
    } else if (options.icon) {
      const namedIconHtml = getIconHtmlByName(options.icon);
      const icon = document.createElement("span");
      icon.className = "toasty-toast__icon";
      if (namedIconHtml) {
        icon.innerHTML = namedIconHtml;
      } else {
        icon.textContent = options.icon;
      }
      toast.appendChild(icon);
    }

    const text = document.createElement("span");
    text.className = "toasty-toast__message";
    text.textContent = options.message;
    toast.appendChild(text);

    if (options.actionText && options.actionUrl) {
      if (options.actionUrl === "close") {
        const actionButton = document.createElement("button");
        actionButton.type = "button";
        actionButton.className = "toasty-toast__action";
        actionButton.textContent = options.actionText;
        actionButton.addEventListener("click", () =>
          closeToastOnClick(toast, options),
        );
        toast.appendChild(actionButton);
      } else {
        const actionButton = document.createElement("a");
        actionButton.className = "toasty-toast__action";
        actionButton.textContent = options.actionText;
        actionButton.href = options.actionUrl;
        actionButton.target = options.actionTarget || "_self";
        if (actionButton.target === "_blank") {
          actionButton.rel = "noopener noreferrer";
        }
        toast.appendChild(actionButton);
      }
    }

    if (options.showClose) {
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "toasty-toast__close";
      closeButton.setAttribute("aria-label", "Close");
      closeButton.textContent = "×";
      closeButton.addEventListener("click", () =>
        closeToastOnClick(toast, options),
      );
      toast.appendChild(closeButton);
    }

    root.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add("is-visible");
    });

    const hideTimer = window.setTimeout(
      () => {
        hideToast(toast, options);
      },
      Math.max(0, options.visibleMs),
    );

    activeToast = { node: toast, hideTimer, cleanupTimer: null };
  };

  window.ToastyToast = { show };
})();
